import React, { useState, useEffect } from "react";
import "./index.scss";
import {
  Button,
  Spin,
  Alert,
  Card,
  Typography,
  Divider,
  Descriptions,
  List,
  Tag,
} from "antd";
import { getUserData } from "@/api/custom";
import { getSignedAgreements, SignedAgreement } from "@/api/ria-agreements";
import { LinkOutlined, CheckCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface UserData {
  name: string;
  email?: string;
  phone?: string;
  id?: string;
  client_id?: string;
  role?: string;
  [key: string]: string | number | boolean | undefined;
}

const Profile: React.FC = () => {
  const [user, setUser] = useState<UserData>({ name: "" });
  const [signedAgreements, setSignedAgreements] = useState<SignedAgreement[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [agreementsLoading, setAgreementsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [agreementsError, setAgreementsError] = useState<string | null>(null);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = getUserData();
      setUser(userData);
    } catch (error) {
      setError("Failed to load user data");
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignedAgreements = async () => {
    try {
      setAgreementsLoading(true);
      const response = await getSignedAgreements();
      // Add null checks to ensure we have valid data
      if (response && Array.isArray(response)) {
        setSignedAgreements(response);
      } else {
        console.warn("Invalid signed agreements response structure:", response);
        setSignedAgreements([]);
      }
    } catch (error) {
      setAgreementsError("Failed to load signed agreements");
      console.error("Error fetching signed agreements:", error);
      setSignedAgreements([]); // Set empty array on error
    } finally {
      setAgreementsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
    fetchSignedAgreements();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatConsentType = (consentType: string) => {
    const consentMap: Record<string, string> = {
      CHECKBOX_ACCEPT: "Checkbox Acceptance",
      SIGNATURE: "Digital Signature",
      CLICK_ACCEPT: "Click to Accept",
      EMAIL_CONFIRM: "Email Confirmation",
    };

    return consentMap[consentType] || consentType;
  };

  if (loading) {
    return (
      <div className="profile">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="profile-container">
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Card title="Personal Information">
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Full Name">
              {user.name || "Not provided"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {user.email || "Not provided"}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Divider />

        <Card
          title="Agreements & Documents"
          extra={
            agreementsLoading ? (
              <Spin size="small" />
            ) : (
              <Text type="secondary">
                {(signedAgreements || []).length} signed agreement(s)
              </Text>
            )
          }
        >
          {agreementsError && (
            <Alert
              message="Error"
              description={agreementsError}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {agreementsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin />
            </div>
          ) : (signedAgreements || []).length > 0 ? (
            <List
              dataSource={signedAgreements || []}
              renderItem={(signedAgreement) => (
                <List.Item
                  style={{
                    padding: "16px 0",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                  }}
                  actions={[
                    <Button
                      type="link"
                      icon={<LinkOutlined />}
                      onClick={() =>
                        window.open(signedAgreement.agreement.url, "_blank")
                      }
                      style={{ padding: 0 }}
                    >
                      View
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                    }}
                    avatar={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "40px",
                          height: "40px",
                          backgroundColor: "#f6ffed",
                          borderRadius: "50%",
                          flexShrink: 0,
                        }}
                      >
                        <CheckCircleOutlined
                          style={{ fontSize: "18px", color: "#52c41a" }}
                        />
                      </div>
                    }
                    title={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Text strong style={{ fontSize: "16px" }}>
                          {signedAgreement.agreement.title}
                        </Text>
                        <Tag color="blue" style={{ margin: 0 }}>
                          v{signedAgreement.agreement.version}
                        </Tag>
                        <Tag color="green" style={{ margin: 0 }}>
                          Signed
                        </Tag>
                      </div>
                    }
                    description={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "24px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: "14px" }}>
                            <strong>Signed:</strong>
                          </Text>
                          <Text style={{ fontSize: "14px" }}>
                            {formatDate(signedAgreement.signature.signed_at)}
                          </Text>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: "14px" }}>
                            <strong>Consent:</strong>
                          </Text>
                          <Text style={{ fontSize: "14px" }}>
                            {formatConsentType(
                              signedAgreement.signature.consent_type,
                            )}
                          </Text>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text type="secondary">No signed agreements found.</Text>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Profile;
